import React, { createContext, useContext, useState, useEffect } from 'react';
import { HeroSlide } from '../types/store';
import {
  subscribeHeroSlidesDb,
  saveHeroSlideDb,
  deleteHeroSlideDb,
  reorderHeroSlidesDb,
  getLocalCachedHeroSlides,
  setLocalCachedHeroSlides,
  INITIAL_HERO_SLIDES,
} from '../lib/firebase';

interface HeroContextType {
  heroSlides: HeroSlide[];
  activeHeroSlides: HeroSlide[];
  isLoading: boolean;
  error: string | null;
  addHeroSlide: (slide: Omit<HeroSlide, 'id'> | HeroSlide) => Promise<void>;
  updateHeroSlide: (slide: HeroSlide) => Promise<void>;
  deleteHeroSlide: (slideId: string, storagePaths?: string[]) => Promise<void>;
  reorderHeroSlides: (slides: HeroSlide[]) => Promise<void>;
  toggleSlideActive: (slideId: string) => Promise<void>;
}

const HeroContext = createContext<HeroContextType | undefined>(undefined);

export const HeroProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [heroSlides, setHeroSlides] = useState<HeroSlide[]>(getLocalCachedHeroSlides());
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = subscribeHeroSlidesDb(
      (slides) => {
        if (slides && slides.length > 0) {
          setHeroSlides(slides);
        } else {
          setHeroSlides(INITIAL_HERO_SLIDES);
        }
        setIsLoading(false);
      },
      (err) => {
        console.warn('Firestore hero slides sync notice:', err);
        setError(err.message);
        setIsLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, []);

  const activeHeroSlides = heroSlides.filter((s) => s.active !== false);

  const addHeroSlide = async (slideData: Omit<HeroSlide, 'id'> | HeroSlide) => {
    const id = 'id' in slideData && slideData.id ? slideData.id : `hero-${Date.now()}`;
    const newSlide: HeroSlide = {
      ...slideData,
      id,
      order: slideData.order || heroSlides.length + 1,
      active: slideData.active !== false,
      createdAt: new Date().toISOString(),
    };

    // Instant optimistic update (0ms delay)
    setHeroSlides((prev) => {
      const updated = [...prev, newSlide].sort((a, b) => (a.order || 0) - (b.order || 0));
      setLocalCachedHeroSlides(updated);
      return updated;
    });

    // Background Firebase Sync
    try {
      await saveHeroSlideDb(newSlide);
    } catch (err: any) {
      console.warn('Background hero slide save notice:', err);
    }
  };

  const updateHeroSlide = async (slide: HeroSlide) => {
    // Instant optimistic update (0ms delay)
    setHeroSlides((prev) => {
      const index = prev.findIndex((s) => s.id === slide.id);
      const updated = index >= 0 ? [...prev] : [...prev, slide];
      if (index >= 0) updated[index] = slide;
      const sorted = updated.sort((a, b) => (a.order || 0) - (b.order || 0));
      setLocalCachedHeroSlides(sorted);
      return sorted;
    });

    // Background Firebase Sync
    try {
      await saveHeroSlideDb(slide);
    } catch (err: any) {
      console.warn('Background hero slide update notice:', err);
    }
  };

  const deleteHeroSlide = async (slideId: string, storagePaths?: string[]) => {
    // Instant optimistic update (0ms delay)
    setHeroSlides((prev) => {
      const updated = prev.filter((s) => s.id !== slideId);
      setLocalCachedHeroSlides(updated);
      return updated;
    });

    try {
      await deleteHeroSlideDb(slideId, storagePaths);
    } catch (err: any) {
      console.warn('Background hero slide deletion notice:', err);
    }
  };

  const reorderHeroSlides = async (slides: HeroSlide[]) => {
    const updated = slides.map((s, idx) => ({ ...s, order: idx + 1 }));
    setHeroSlides(updated);
    setLocalCachedHeroSlides(updated);

    try {
      await reorderHeroSlidesDb(slides);
    } catch (err: any) {
      console.warn('Background hero slide reorder notice:', err);
    }
  };

  const toggleSlideActive = async (slideId: string) => {
    const target = heroSlides.find((s) => s.id === slideId);
    if (!target) return;
    const updatedSlide = {
      ...target,
      active: !target.active,
    };

    setHeroSlides((prev) => {
      const updated = prev.map((s) => (s.id === slideId ? updatedSlide : s));
      setLocalCachedHeroSlides(updated);
      return updated;
    });

    try {
      await saveHeroSlideDb(updatedSlide);
    } catch (err: any) {
      console.warn('Background hero slide toggle notice:', err);
    }
  };

  return (
    <HeroContext.Provider
      value={{
        heroSlides,
        activeHeroSlides,
        isLoading,
        error,
        addHeroSlide,
        updateHeroSlide,
        deleteHeroSlide,
        reorderHeroSlides,
        toggleSlideActive,
      }}
    >
      {children}
    </HeroContext.Provider>
  );
};

export const useHero = () => {
  const context = useContext(HeroContext);
  if (!context) {
    throw new Error('useHero must be used within a HeroProvider');
  }
  return context;
};
